import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { Footer } from './Footer';
import { ErrorBoundary } from './ErrorBoundary';
import { AnalyticsTracker } from '@/components/AnalyticsTracker';
import { useAuthStore } from '@/stores/authStore';
import { isAdminRole, isFullAdminRole } from '@/lib/constants';
import { bootIntercom } from '@/lib/intercom';

// ENDO-1112: Verified — sidebar is intentionally hidden on Edit Hormones,
// Edit Levels, and Comment Triggers to match WP admin behavior.
// Pages that don't show the admin sidebar
const NO_SIDEBAR_PATHS = ['/admin/hormones', '/admin/hormone-levels', '/admin/triggers', '/patients/'];

// Admin review page (/patients/:id/review/:labResultId) SHOULD show the sidebar
const SIDEBAR_OVERRIDE_PATTERN = /^\/patients\/\d+\/review\//;

export function AppLayout() {
  const { user } = useAuthStore();
  const location = useLocation();
  const isAdmin = isAdminRole(user?.roleId);
  const isFullAdmin = isFullAdminRole(user?.roleId);

  // Boot Intercom on mount — with user info if logged in, anonymous otherwise
  useEffect(() => {
    if (user) {
      bootIntercom({
        name: `${user.fname ?? ''} ${user.lname ?? ''}`.trim(),
        email: user.usr ?? '',
        userId: user.uid ? Number(user.uid) : undefined,
      });
    } else {
      bootIntercom();
    }
  }, [user]);
  const forceShowSidebar = SIDEBAR_OVERRIDE_PATTERN.test(location.pathname);
  const hideSidebar = !forceShowSidebar && NO_SIDEBAR_PATHS.some((p) => location.pathname.startsWith(p));
  const showSidebar = isFullAdmin && !hideSidebar;

  return (
    <div className="flex min-h-screen flex-col bg-ea-warm-white">
      <AnalyticsTracker />
      <div className="no-print">
        <Header />
      </div>
      <div className="flex flex-1">
        {showSidebar && (
          <div className="no-print">
            <Sidebar />
          </div>
        )}
        <main className={`flex-1 px-4 py-5 lg:px-5 ${showSidebar ? 'ml-48' : ''} print:ml-0 print:p-0`}>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
      {!isAdmin && (
        <div className="no-print">
          <Footer />
        </div>
      )}
    </div>
  );
}
